/**
 * Sorting the projects list on a column (owner 2026-11-04): a header click sorts ascending, again descending, a third
 * time back to the list's own order (client A–Ö, then project). One choice for the whole list, applied within each
 * delivery month; rows without a value always come last; the videos under a row follow their project.
 * The choice lives in the URL (`sort=deadline`, `sort=-deadline`) and is remembered on the device.
 */
export const SORT_KEYS = ['project', 'stage', 'next', 'deadline', 'shoot', 'producer', 'editor', 'camera'] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type ListSort = { key: SortKey; dir: 'asc' | 'desc' } | null;

/** What a row needs to be sorted: the projects list's own summary fits. */
export interface SortRow {
  name: string;
  stage: { position: number };
  next: { phrase: string; mine: boolean };
  myTurn?: boolean;
  nextDeadline: string | null;
  shoot?: { date: string } | null;
  people?: { name: string; role: string; roles?: string[] }[];
}

const STORE = 'kff.projects.sort';

export const parseSort = (s: string | null | undefined): ListSort => {
  if (!s) return null;
  const desc = s.startsWith('-');
  const key = (desc ? s.slice(1) : s) as SortKey;
  return SORT_KEYS.includes(key) ? { key, dir: desc ? 'desc' : 'asc' } : null;
};
export const sortParam = (s: ListSort) => (s ? `${s.dir === 'desc' ? '-' : ''}${s.key}` : '');

/** The URL first (a shared link), else what this device chose last. */
export const readSort = (search: string): ListSort => {
  const q = new URLSearchParams(search).get('sort');
  if (q !== null) return parseSort(q);
  try {
    return parseSort(localStorage.getItem(STORE));
  } catch {
    return null;
  }
};
export const saveSort = (s: ListSort) => {
  try {
    if (s) localStorage.setItem(STORE, sortParam(s));
    else localStorage.removeItem(STORE);
  } catch {
    /* private mode: the choice lasts until the page closes */
  }
};

/** A header click: ascending, then descending, then off. */
export const nextSort = (cur: ListSort, key: SortKey): ListSort => (cur?.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null);

const firstOf = (x: SortRow, role: string) => (x.people ?? []).find((m) => (m.roles ?? [m.role]).includes(role))?.name ?? null;

const valueOf = (x: SortRow, key: SortKey): string | number | null => {
  switch (key) {
    case 'project':
      return x.name;
    case 'stage':
      return x.stage.position;
    case 'next':
      // "Din tur" first, then the step's words A–Ö
      return `${(x.myTurn ?? x.next.mine) ? 0 : 1}${x.next.phrase}`;
    case 'deadline':
      return x.nextDeadline;
    case 'shoot':
      return x.shoot?.date ?? null;
    default:
      return firstOf(x, key);
  }
};

/** The rows in the chosen order; stable, so equal rows keep the list's own order. Without a choice, the rows as they came. */
export const sortRows = <T extends SortRow>(rows: T[], s: ListSort): T[] => {
  if (!s) return rows;
  const sign = s.dir === 'desc' ? -1 : 1;
  return rows
    .map((r, i) => ({ r, i, v: valueOf(r, s.key) }))
    .sort((a, b) => {
      if (a.v === null || a.v === '') return b.v === null || b.v === '' ? a.i - b.i : 1;
      if (b.v === null || b.v === '') return -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v), 'sv', { sensitivity: 'base', numeric: true });
      return c * sign || a.i - b.i;
    })
    .map((x) => x.r);
};
